<template>
  <div aria-hidden="true" class="FondLayerHandler" style="display: none"></div>
</template>

<script setup lang="ts">
import type { Map as MaplibreMap, MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl'
import { computed, createApp, h, watch, onUnmounted } from 'vue'
import type Map from '@/components/map/Map.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import { CommuneService, getCommuneGeometryFromApi } from '@/services/commune/CommuneService'
import {
  FondLayerService,
  clearHighlight,
  setHighlightFeature,
} from '@/services/map/FondLayerService'
import IndicatorPopup from '@/views/map/components/IndicatorPopup.vue'
import type { CommuneData } from '@/views/map/components/CommuneCard.vue'

const props = defineProps<{
  myMap: InstanceType<typeof Map> | undefined
  mapInstance: MaplibreMap | null
}>()

const myMapStore = useMyMapStore()

/** Fond layer style IDs that the user has turned on via the card toggle. Layers are loaded only for these. */
const activeFondStyleIds = computed(() => [...myMapStore.visibleFondStyleIds])

const attachedStyleIds = new Set<string>()

/** Currently highlighted polygon (by key) for click-to-toggle. Cleared when toggling same feature. */
let highlightedFeatureKey: string | null = null

function getFeatureKey(feature: MapGeoJSONFeature): string {
  if (feature.id != null) return `id:${feature.id}`
  return `geom:${JSON.stringify(feature.geometry)}`
}

function getFeatureLabel(properties: Record<string, unknown>): string {
  const raw = (properties.cmn_titre ??
    properties.nom ??
    properties.name ??
    properties.Commune ??
    properties.commune) as string | undefined
  return raw != null ? String(raw).trim() : ''
}

function toggleHighlightOnClick(
  map: Parameters<typeof clearHighlight>[0],
  feature: MapGeoJSONFeature,
  label: string,
): void {
  if (!map) return
  const key = getFeatureKey(feature)
  if (highlightedFeatureKey === key) {
    clearHighlight(map)
    highlightedFeatureKey = null
    return
  }
  setHighlightFeature(map, feature, label)
  highlightedFeatureKey = key
}

interface ClickHandlers {
  main: (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => void
  onFillLayer: (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => void
  onOtherLayers: (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => void
}
const clickHandlersByStyleId = new globalThis.Map<string, ClickHandlers>()

function buildCommuneDataFromFeature(
  properties: Record<string, unknown>,
  featureId?: string | number,
): CommuneData {
  const code = (properties.cmn_code ??
    properties.code_commune ??
    properties.Code ??
    properties.code ??
    properties.CODCOM ??
    properties.codcom ??
    properties.code_insee ??
    properties.commune_code ??
    properties.cmnCode) as string | undefined
  const titre = (properties.cmn_titre ||
    properties.nom ||
    properties.name ||
    properties.Commune ||
    properties.commune ||
    'Commune') as string
  const dept = (properties.cmn_dept ||
    properties.departement ||
    properties.department ||
    properties.Département ||
    properties.dept) as string | undefined
  const region = (properties.cmn_region || properties.region || properties.Région) as
    | string
    | undefined
  const population =
    properties.cmn_population !== undefined
      ? Number(properties.cmn_population)
      : properties.population !== undefined
        ? Number(properties.population)
        : undefined
  const superficie =
    properties.cmn_superficie !== undefined
      ? Number(properties.cmn_superficie)
      : properties.superficie !== undefined
        ? Number(properties.superficie)
        : undefined
  const boitePostale = (properties.cmn_boite_postale ||
    properties.boite_postale ||
    properties.bp ||
    properties.adresse ||
    properties.address) as string | undefined
  const siteWeb = (properties.cmn_site_web ||
    properties.site_web ||
    properties.site ||
    properties.website) as string | undefined
  const anneeDebut =
    properties.cmn_annee_debut !== undefined
      ? Number(properties.cmn_annee_debut)
      : properties.annee_debut !== undefined
        ? Number(properties.annee_debut)
        : properties.annee !== undefined || properties.year !== undefined
          ? Number(properties.annee ?? properties.year)
          : undefined
  const anneeFin =
    properties.cmn_annee_fin !== undefined
      ? Number(properties.cmn_annee_fin)
      : properties.annee_fin !== undefined
        ? Number(properties.annee_fin)
        : undefined
  const rawId = (properties.id ??
    properties['@id'] ??
    (featureId != null ? String(featureId) : undefined)) as string | undefined
  const id =
    typeof rawId === 'string' && rawId
      ? rawId.includes('/')
        ? rawId.replace(/.*\//, '').replace(/\?.*$/, '')
        : rawId
      : undefined

  return {
    id,
    cmn_code: code,
    cmn_titre: titre,
    cmn_dept: dept,
    cmn_region: region,
    cmn_population: population,
    cmn_statut: (properties.cmn_statut ?? properties.statut) as string | undefined,
    cmn_boite_postale: boitePostale,
    cmn_site_web: siteWeb,
    cmn_superficie: superficie,
    cmn_densite: (properties.cmn_densite || properties.densite || properties.density) as
      | string
      | undefined,
    cmn_email_marie: (properties.cmn_email_marie || properties.couriel || properties.email) as
      | string
      | undefined,
    cmn_maire: (properties.cmn_maire || properties.maire || properties.mayor) as string | undefined,
    cmn_telephone: (properties.cmn_telephone || properties.telephone || properties.phone) as
      | string
      | undefined,
    cmn_nbr_conseiller:
      properties.cmn_nbr_conseiller !== undefined
        ? Number(properties.cmn_nbr_conseiller)
        : undefined,
    cmn_nbr_conseiller_femme:
      properties.cmn_nbr_conseiller_femme !== undefined
        ? Number(properties.cmn_nbr_conseiller_femme)
        : undefined,
    cmn_nbr_adjoint:
      properties.cmn_nbr_adjoint !== undefined ? Number(properties.cmn_nbr_adjoint) : undefined,
    cmn_nbr_adjoint_femme:
      properties.cmn_nbr_adjoint_femme !== undefined
        ? Number(properties.cmn_nbr_adjoint_femme)
        : undefined,
    cmn_date_creation: (properties.cmn_date_creation ??
      properties.date_creation ??
      properties.date_creation_commune) as string | undefined,
    cmn_annee_debut: anneeDebut,
    cmn_annee_fin: anneeFin,
    nom: titre,
    departement: dept,
    region,
    habitants: population,
    superficie,
    densite: (properties.cmn_densite || properties.densite || properties.density) as
      | string
      | number
      | undefined,
  }
}

function attachClickHandler(styleId: string): void {
  // Commune layer clicks are handled entirely by CommuneLayerHandler
  if (styleId === 'commune') return

  const map = props.mapInstance
  if (!map) {
    console.warn('[FondLayer] attachClickHandler: no map', styleId)
    return
  }

  const layerIds = FondLayerService.getLayerIds(styleId)
  if (layerIds.length === 0) {
    console.warn(
      '[FondLayer] attachClickHandler: no layerIds for style',
      styleId,
      '- click will not work',
    )
    return
  }

  const popupMeta = FondLayerService.getPopupMetadata(styleId)
  const popupTitle = popupMeta?.title ?? ''
  const popupAttribute = popupMeta?.attribute ?? 'value'
  const popupAttributes = popupMeta?.attributes

  const ficheType =
    myMapStore.fondLayerStyles.find((s) => s.id === styleId)?.metadata?.fiche_type ??
    (styleId === 'commune' ? 'commune' : undefined)

  const mainHandler = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
    if (!e.features?.length) return

    const feature = e.features[0]
    const properties = (feature.properties || {}) as Record<string, unknown>

    if (ficheType === 'commune') {
      const featureId = feature.id != null ? feature.id : undefined
      const communeData = buildCommuneDataFromFeature(properties, featureId)
      console.log('[FondLayerHandler] commune click', {
        styleId,
        commune: communeData.cmn_titre || communeData.nom,
        featureId,
        lngLat: e.lngLat ? [e.lngLat.lng, e.lngLat.lat] : undefined,
      })
      myMapStore.openCommuneFicheFromFondLayer(communeData)
      return
    }

    if (!myMapStore.myMap?.addPopup) return

    const attributsRows =
      popupAttributes?.length &&
      popupAttributes.every((a) => typeof a.label === 'string' && typeof a.attribute === 'string')
        ? popupAttributes.map((a) => ({
            label: a.label,
            value: String(
              (properties[a.attribute] as string | number | boolean | null | undefined) ?? '',
            ),
          }))
        : undefined

    const singleValue =
      attributsRows == null
        ? ((properties[popupAttribute] as string | number | boolean | undefined) ?? null)
        : undefined

    const popupComponent = h(IndicatorPopup, {
      title: popupTitle || undefined,
      value: singleValue ?? (attributsRows != null ? '' : undefined),
      attributsRows,
    })
    const container = document.createElement('div')
    const app = createApp({ render: () => popupComponent })
    app.mount(container)

    myMapStore.myMap.addPopup(e.lngLat, container, false)
  }

  const fillLayerId = FondLayerService.getFillLayerId(styleId)
  const onFillLayerClick = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
    if (!e.features?.[0]) {
      mainHandler(e)
      return
    }
    const feature = e.features[0]
    const properties = (feature.properties || {}) as Record<string, unknown>
    const label = getFeatureLabel(properties)

    if (ficheType === 'commune') {
      const communeData = buildCommuneDataFromFeature(
        properties,
        feature.id != null ? feature.id : undefined,
      )
      console.log('[FondLayerHandler] commune click (fill layer)', {
        styleId,
        commune: label || communeData.cmn_titre || communeData.nom,
        featureId: feature.id,
        lngLat: e.lngLat ? [e.lngLat.lng, e.lngLat.lat] : undefined,
      })
      myMapStore.openCommuneFicheFromFondLayer(communeData)
      const key = getFeatureKey(feature)
      if (highlightedFeatureKey === key) {
        clearHighlight(map)
        highlightedFeatureKey = null
        return
      }
      void (async () => {
        const api = communeData.id
          ? await CommuneService.getCommuneById(communeData.id)
          : communeData.cmn_code
            ? await CommuneService.getCommuneByCode(communeData.cmn_code)
            : null
        const geometry = getCommuneGeometryFromApi(api ?? null)
        if (geometry) {
          setHighlightFeature(
            map,
            { type: 'Feature', properties: {}, geometry } as MapGeoJSONFeature,
            label,
          )
        } else {
          setHighlightFeature(map, feature, label)
        }
        highlightedFeatureKey = key
      })()
      return
    }

    toggleHighlightOnClick(map, feature, label)
    mainHandler(e)
  }
  const onOtherLayersClick = mainHandler

  clickHandlersByStyleId.set(styleId, {
    main: mainHandler,
    onFillLayer: onFillLayerClick,
    onOtherLayers: onOtherLayersClick,
  })

  const onMouseEnter = () => { map.getCanvas().style.cursor = 'pointer' }
  const onMouseMove = () => { map.getCanvas().style.cursor = 'pointer' }
  const onMouseLeave = () => { map.getCanvas().style.cursor = '' }

  for (const layerId of layerIds) {
    if (!map.getLayer(layerId)) continue
    const isFillLayer = layerId === fillLayerId
    map.on('click', layerId, isFillLayer ? onFillLayerClick : onOtherLayersClick)
    if (isFillLayer) {
      map.on('mousemove', layerId, onMouseMove)
    } else {
      map.on('mouseenter', layerId, onMouseEnter)
    }
    map.on('mouseleave', layerId, onMouseLeave)
  }

  cursorHandlersByStyleId.set(styleId, { onMouseEnter, onMouseMove, onMouseLeave })
}

const cursorHandlersByStyleId = new globalThis.Map<string, { onMouseEnter: () => void; onMouseMove: () => void; onMouseLeave: () => void }>()

function detachClickHandler(styleId: string): void {
  if (styleId === 'commune') return

  const map = props.mapInstance
  if (!map) return

  const handlers = clickHandlersByStyleId.get(styleId)
  const cursorHandlers = cursorHandlersByStyleId.get(styleId)
  const layerIds = FondLayerService.getLayerIds(styleId)
  const fillLayerId = FondLayerService.getFillLayerId(styleId)

  if (handlers) {
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        const isFillLayer = layerId === fillLayerId
        map.off('click', layerId, isFillLayer ? handlers.onFillLayer : handlers.onOtherLayers)
      }
    }
  }
  if (cursorHandlers) {
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        const isFillLayer = layerId === fillLayerId
        if (isFillLayer) {
          map.off('mousemove', layerId, cursorHandlers.onMouseMove)
        } else {
          map.off('mouseenter', layerId, cursorHandlers.onMouseEnter)
        }
        map.off('mouseleave', layerId, cursorHandlers.onMouseLeave)
      }
    }
    cursorHandlersByStyleId.delete(styleId)
  }
  map.getCanvas().style.cursor = ''
  clearHighlight(map)
  highlightedFeatureKey = null
  clickHandlersByStyleId.delete(styleId)
}

async function ensureLayersForActiveStyles(): Promise<void> {
  const myMap = props.myMap
  const map = props.mapInstance
  const activeIds = activeFondStyleIds.value
  if (!map || !myMap) {
    return
  }

  const toAdd = activeIds.filter((id) => !attachedStyleIds.has(id))
  const toRemove = [...attachedStyleIds].filter((id) => !activeIds.includes(id))

  for (const styleId of toRemove) {
    detachClickHandler(styleId)
    FondLayerService.removeFondLayerFromMap(myMap, styleId)
    attachedStyleIds.delete(styleId)
  }

  for (const styleId of toAdd) {
    attachedStyleIds.add(styleId)
    const hadLayer = FondLayerService.hasLayer(styleId)
    if (!hadLayer) {
      await FondLayerService.addFondLayerToMap(myMap, styleId)
    }
    attachClickHandler(styleId)
  }
  for (const styleId of activeIds) {
    FondLayerService.setFondLayerVisibility(myMap, styleId, true)
  }
}

watch(
  [() => props.mapInstance, activeFondStyleIds],
  async () => {
    await ensureLayersForActiveStyles()
  },
  { immediate: true },
)

onUnmounted(() => {
  const myMap = props.myMap
  for (const styleId of attachedStyleIds) {
    detachClickHandler(styleId)
    if (myMap) FondLayerService.removeFondLayerFromMap(myMap, styleId)
  }
  attachedStyleIds.clear()
  clickHandlersByStyleId.clear()
  cursorHandlersByStyleId.clear()
})
</script>
